import React, { ReactNode, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../navigation/Navbar";
import Sidebar from "../../navigation/Sidebar";
import { SidebarItem } from "../../navigation/SidebarItem";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import useStore from "hooks/useStore";

interface LayoutProps {
  children: ReactNode;
}
interface SubMenue {
  title: string;
  path: string;
  resource?: string;
}
interface SidebarItemType {
  text: string;
  path: string;
  // Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  submenu?: SubMenue[];
  resource?: string;
}

const MainLayout: React.FC<LayoutProps> = ({ children }) => {
  const { hasResourceAccess } = useStore();
  const location = useLocation();
  const isSidebarExpanded = useSelector(
    (state: any) => state.sharedReducer?.isSidebarExpanded ?? true
  );
  
  // Track which submenu is currently open (accordion behavior)
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);

  // Define the sidebar data
  const sidebarData: SidebarItemType[] = [
    {
      text: "Dashboard",
      path: siteRoutes.home,
      resource: "dashboard", // Resource for access control
    },
    {
      text: "User Management",
      path: "/user-management",
      submenu: [
        {
          title: "Attendees",
          path: siteRoutes.attendess,
          resource: "attendee",
        },
        {
          title: "Hosts",
          path: siteRoutes.hosts,
          resource: "host",
        },
        {
          title: "Ambassadors",
          path: siteRoutes.ambassadors,
          resource: "ambassador",
        },
        {
          title: "Minors",
          path: siteRoutes.minors,
          resource: "minor",
        },
        {
          title: "RSVPs",
          path: siteRoutes.rsvps,
          resource: "rsvp",
        },
      ],
    },
    {
      text: "Host Management",
      path: "/host-management",
      submenu: [
        {
          title: "Manage Subscriptions",
          path: siteRoutes.hostSubscription,
          // resource: "host-subscription",
        },
        {
          title: "Cancel Events",
          path: siteRoutes.hostCancelEventsListing,
          // resource: "host-subscriber",
        },
      ],
    },
    {
      text: "Communities",
      path: siteRoutes.communitiesListing,
      resource: "communities",
    },
    {
      text: "Business Management",
      path: siteRoutes.businessManagementListing,
      resource: "business-management",
    },
    {
      text: "Experiences",
      path: siteRoutes.experiences,
      resource: "experiences",
    },
    {
      text: "Badges Management",
      path: "/badges-management",

      submenu: [
        {
          title: "Event Badges",
          path: siteRoutes.eventBadgeListing,
          resource: "event-badges",
        },
        {
          title: "Achievement Badges",
          path: siteRoutes.achievementBadgeListing,
          resource: "achievement-badges",
        },
        {
          title: "Core Concept Badges",
          path: siteRoutes.coreConceptBadgeListing,
          resource: "core-concept-badges",
        },
      ],
    },
    {
      text: "Resource Library",
      path: siteRoutes.resourceLibraryListing,
      resource: "resource-library",
    },
    {
      text: "Policy & Agreement",
      path: siteRoutes.policyAgreement,
      resource: "policy-agreement",
    },
    {
      text: "Cup of Gratitude",
      path: siteRoutes.cupOfGratitudeListing,
      resource: "cup-of-gratitude",
    },

    {
      text: "Team Management",
      path: "/team-management",

      submenu: [
        {
          title: "Team Members",
          path: siteRoutes.teamMemberListing,
          resource: "team-member",
        },
        {
          title: "Role Management",
          path: siteRoutes.roleManagementListing,
          resource: "role-management",
        },
        // {
        //   title: "Ambassadors",
        //   path: "/system-configuration/api-and-database",
        // },
      ],
    },

    {
      text: "Master Portal",
      path: "/master-portal",
      resource: "master-portal",

      submenu: [
        {
          title: "Product Items",
          path: siteRoutes.productItems,
          resource: "product-items",
        },
        {
          title: "Order Requests",
          path: siteRoutes.orderRequests,
          resource: "order-requests",
        },
      ],
    },
  ];

  // Automatically open submenu if one of its items is active
  useEffect(() => {
    const filteredData = sidebarData.filter((item: SidebarItemType) => {
      if (item.resource) return hasResourceAccess(item?.resource);
      if (item.submenu) {
        const filteredSubmenu = item.submenu.filter(
          (sub) => !sub?.resource || hasResourceAccess(sub?.resource)
        );
        return filteredSubmenu.length > 0;
      }
      return true;
    });

    // Find which submenu (if any) should be open based on current route
    let foundIndex: number | null = null;
    filteredData.forEach((item: SidebarItemType, index: number) => {
      if (item.submenu && item.submenu.some((sub) => location.pathname.startsWith(sub.path))) {
        foundIndex = index;
      }
    });

    // Update state only if different from current state
    setOpenSubmenuIndex((prevIndex) => {
      if (foundIndex !== prevIndex) {
        return foundIndex;
      }
      return prevIndex;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="h-screen xl:bg-white">
      <div className="flex flex-row-reverse h-full overflow-hidden">
        <div
          className={`${
            isSidebarExpanded ? "w-[calc(100%-272px)]" : "w-full"
          } h-full`}
        >
          <Navbar />
          <main className="bg-offWhiteBg w-full h-[calc(100%-80px)] overflow-y-auto p-4 sm:p-6">
            {children}
          </main>
        </div>
        <Sidebar>
          {sidebarData
            .filter((item: SidebarItemType) => {
              // Show if item has no resource or user has access
              if (item.resource) return hasResourceAccess(item?.resource);

              // If submenu exists, check if at least one submenu item is accessible
              if (item.submenu) {
                item.submenu = item.submenu.filter(
                  (sub) => !sub?.resource || hasResourceAccess(sub?.resource)
                );
                return item.submenu.length > 0;
              }

              return true; // Show by default if no restrictions
            })
            .map((element: SidebarItemType, index: number) => {
              const isActive =
                element.path === "/"
                  ? location.pathname === "/" ||
                    location.pathname.includes("/dashboard")
                  : location.pathname.startsWith(element.path);

              const hasSubmenu = element.submenu && element.submenu.length > 0;
              const isSubmenuOpen = openSubmenuIndex === index;

              return (
                <SidebarItem
                  text={element.text}
                  active={isActive}
                  link={element.path}
                  key={index}
                  submenu={element.submenu}
                  isSubmenuOpen={hasSubmenu ? isSubmenuOpen : false}
                  onSubmenuToggle={() => {
                    if (hasSubmenu) {
                      // Toggle: close if already open, open if closed
                      setOpenSubmenuIndex(isSubmenuOpen ? null : index);
                    }
                  }}
                />
              );
            })}
        </Sidebar>
      </div>
    </div>
  );
};

export default MainLayout;
